// controllers/salesOrderController.js  (frontend)
import apiService from '../services/apiService';
import { authController } from './authController';

const _getUser = () => authController.getCurrentUser();
const _getRole = () => _getUser()?.role || null;

const _guard = (allowedRoles) => {
  const role = _getRole();
  if (!role)                        return { blocked: true, reason: 'Not authenticated. Please log in.' };
  if (!allowedRoles.includes(role)) return { blocked: true, reason: `Access denied. Requires: ${allowedRoles.join(' or ')}` };
  return { blocked: false };
};

const ALLOWED = ['admin', 'super_admin', 'sales_manager', 'sales_rep'];

const salesOrderController = {
  getCurrentUser: () => _getUser(),
  getCurrentRole: () => _getRole(),
  isSuperAdmin:   () => _getRole() === 'super_admin',
  isAdmin:        () => ['admin', 'super_admin'].includes(_getRole()),

  getAllOrders: async (filters = {}) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, data: [], message: g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `sales-orders?${qs}` : 'sales-orders');
      return { success: true, data: res.data || [], pagination: res.pagination || {} };
    } catch (e) { return { success: false, data: [], message: e.message || 'Failed to fetch orders' }; }
  },

  getOrderById: async (id) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const res = await apiService.get(`sales-orders/${id}`);
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message || 'Order not found' }; }
  },

  createOrder: async (data) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post('sales-orders', data);
      return { success: true, data: res.data, message: 'Sales order created successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to create order' }; }
  },

  updateOrder: async (id, updates) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.put(`sales-orders/${id}`, updates);
      return { success: true, data: res.data, message: 'Order updated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to update order' }; }
  },

  // Pending -> Processing -> Shipped -> Delivered / Cancelled
  updateStatus: async (id, status) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`sales-orders/${id}/status`, { status });
      return { success: true, data: res.data, message: 'Status updated successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to update status' }; }
  },

  convertToInvoice: async (id, invoiceData = {}) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.post(`sales-orders/${id}/convert-to-invoice`, invoiceData);
      return { success: true, data: res.data, message: 'Converted to Invoice successfully' };
    } catch (e) { return { success: false, message: e.message || 'Conversion failed' }; }
  },

  cancelOrder: async (id, cancelReason = '') => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      const res = await apiService.patch(`sales-orders/${id}/cancel`, { cancelReason });
      return { success: true, data: res.data, message: 'Order cancelled successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to cancel order' }; }
  },

  deleteOrder: async (id) => {
    const g = _guard(['super_admin', 'admin']);
    if (g.blocked) return { success: false, message: g.reason };
    try {
      await apiService.delete(`sales-orders/${id}`);
      return { success: true, message: 'Order deleted successfully' };
    } catch (e) { return { success: false, message: e.message || 'Failed to delete order' }; }
  },

  getStatistics: async (filters = {}) => {
    const g = _guard(ALLOWED);
    if (g.blocked) return { success: false, data: null, message: g.reason };
    try {
      const qs  = new URLSearchParams(filters).toString();
      const res = await apiService.get(qs ? `sales-orders/statistics?${qs}` : 'sales-orders/statistics');
      return { success: true, data: res.data };
    } catch (e) { return { success: false, data: null, message: e.message }; }
  },
};

export default salesOrderController;